import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { CommunicationTemplate, getTemplatesByType, saveTemplate, deleteTemplate } from '@/services/communication/templates';
import { getLeadById } from '@/services/leads/queries/getLeadById';
import { EmailWithType } from '@/types/lead';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Lock, FileText } from 'lucide-react';
import TemplateForm from './TemplateForm';
import TemplateList from './TemplateList';
import TemplateTabs from './TemplateTabs';
import { canAccessCallScripts } from '@/services/subscriptions/access';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';
import { SendEmailDialog } from './SendEmailDialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TemplateManagerProps {
  type: 'email' | 'call';
  onSelectTemplate: (template: CommunicationTemplate) => void;
  leadId?: string;
}

const TemplateManager: React.FC<TemplateManagerProps> = ({ 
  type, 
  onSelectTemplate,
  leadId 
}) => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Partial<CommunicationTemplate> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [templateForEmail, setTemplateForEmail] = useState<CommunicationTemplate | null>(null);

  // Fetch lead data if leadId is provided
  const { data: leadData } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => getLeadById(leadId!),
    enabled: !!leadId,
  });

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await getTemplatesByType(type, undefined, leadId);
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load templates. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
    fetchTemplates();
  }, [type, leadId]);

  const checkAccess = async () => {
    if (type === 'call') {
      const access = await canAccessCallScripts();
      setHasAccess(access);
    } else {
      setHasAccess(true);
    }
  };

  const handleSaveTemplate = async (templateData: Partial<CommunicationTemplate>) => {
    try {
      const savedTemplate = await saveTemplate({
        ...templateData,
        type,
        leadId
      });
      
      if (savedTemplate) {
        toast({
          title: "Success",
          description: `Template ${templateData.id ? 'updated' : 'created'} successfully.`
        });
        
        fetchTemplates();
        setIsCreating(false);
        setSelectedTemplate(null);
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save template. Please try again."
      });
    }
  };

  const confirmDeleteTemplate = (id: string) => {
    setTemplateToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      const success = await deleteTemplate(templateToDelete);
      if (success) {
        toast({
          title: "Success",
          description: "Template deleted successfully."
        });
        
        setTemplates(templates.filter(t => t.id !== templateToDelete));
      } else {
        throw new Error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete template. Please try again."
      });
    } finally {
      setTemplateToDelete(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleEditTemplate = (template: CommunicationTemplate) => {
    setSelectedTemplate(template);
    setIsCreating(true);
  };

  const handleCancelForm = () => {
    setIsCreating(false);
    setSelectedTemplate(null);
  };

  const handleSendEmail = (template: CommunicationTemplate) => {
    setTemplateForEmail(template);
    setEmailDialogOpen(true);
  };

  const handleCopyToClipboard = async (template: CommunicationTemplate) => {
    try {
      // For email templates, extract just the body (without subject)
      const contentToCopy = type === 'email' 
        ? extractBodyFromContent(template.content)
        : template.content;
      
      await navigator.clipboard.writeText(contentToCopy);
      
      toast({
        title: "Copied to clipboard",
        description: type === 'email' 
          ? "Email body copied. You can now paste it into another email platform."
          : "Call script copied to clipboard.",
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Failed to copy to clipboard. Please try again.",
      });
    }
  };

  const getLeadEmailString = (email: string | EmailWithType | EmailWithType[] | undefined): string => {
    if (!email) return '';
    
    // If it's already a string, return it
    if (typeof email === 'string') return email;
    
    // If it's an array of email objects
    if (Array.isArray(email)) {
      if (email.length === 0) return '';
      
      // Try to find a work/business email first
      const workEmail = email.find(e => 
        e.type?.toLowerCase().includes('work') || 
        e.type?.toLowerCase().includes('business')
      );
      
      if (workEmail) return workEmail.email;
      
      // Otherwise return the first email
      return email[0].email;
    }
    
    // If it's a single EmailWithType object
    if (typeof email === 'object' && 'email' in email) {
      return email.email;
    }
    
    return '';
  };

  const extractSubjectFromContent = (content: string): string => {
    const lines = content.split('\n');
    
    // Look for lines that contain a subject in various formats
    const subjectLine = lines.find(line => {
      const trimmedLine = line.trim();
      return /^\*\*subject(\s+line)?:\*\*/i.test(trimmedLine) || 
             /^subject(\s+line)?:/i.test(trimmedLine);
    });
    
    if (subjectLine) {
      // Extract the actual subject text after the label
      return subjectLine
        .replace(/^\*\*subject(\s+line)?:\*\*/i, '')
        .replace(/^subject(\s+line)?:/i, '')
        .trim();
    }
    
    return '';
  };

  const extractBodyFromContent = (content: string): string => {
    const lines = content.split('\n');
    
    // Find the index of the subject line
    const subjectIndex = lines.findIndex(line => {
      const trimmedLine = line.trim();
      return /^\*\*subject(\s+line)?:\*\*/i.test(trimmedLine) || 
             /^subject(\s+line)?:/i.test(trimmedLine);
    });
    
    if (subjectIndex >= 0) {
      // Start from after the subject line
      let bodyStartIndex = subjectIndex + 1;
      
      // Skip any separator lines (---, ***, etc.) and empty lines
      while (bodyStartIndex < lines.length) {
        const line = lines[bodyStartIndex].trim();
        if (line === '' || /^[-*=]{3,}$/.test(line)) {
          bodyStartIndex++;
        } else {
          break;
        }
      }
      
      // Return the body from the first content line onwards
      return lines.slice(bodyStartIndex).join('\n').trim();
    }
    
    // If no subject line found, return the original content
    return content;
  };

  const filterTemplates = (templates: CommunicationTemplate[], filter: string) => {
    if (filter === 'all') return templates;
    if (filter === 'default') return templates.filter(t => t.isDefault);
    return templates.filter(t => t.leadStatus === filter);
  };

  const filteredTemplates = filterTemplates(templates, activeTab);

  if (hasAccess === null) {
    return (
      <Card className="w-full">
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading...
        </CardContent>
      </Card>
    );
  }

  if (!hasAccess && type === 'call') {
    return (
      <UpgradePrompt
        feature="Call Scripts Available on Starter Plan"
        description="Upgrade to Starter or higher to access AI-generated call script templates."
        tier="starter"
        icon={<Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />}
        benefits={[
          'Save and manage call script templates',
          'Customize scripts for different lead statuses',
          'Quick access to reusable call scripts',
          'Access to 100 leads per month'
        ]}
      />
    );
  }

  return (
    <TooltipProvider>
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {type === 'email' ? 'Email Templates' : 'Call Scripts'}
              </CardTitle>
              <CardDescription>
                {type === 'email' 
                  ? 'Save and manage your email templates for quick use' 
                  : 'Save and manage your call scripts for quick reference'}
              </CardDescription>
            </div>
            <Dialog
              open={isCreating}
              onOpenChange={(open) => {
                setIsCreating(open);
                if (open) setSelectedTemplate(null);
              }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 border-2"
                      aria-label={`New ${type === 'email' ? 'Email Template' : 'Call Script'}`}
                    >
                      <Plus className="h-5 w-5" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>New {type === 'email' ? 'Email Template' : 'Call Script'}</p>
                </TooltipContent>
              </Tooltip>
              <DialogContent className="sm:max-w-[700px]">
                <DialogHeader>
                  <DialogTitle>
                    {selectedTemplate ? 'Edit Template' : `Create New ${type === 'email' ? 'Email Template' : 'Call Script'}`}
                  </DialogTitle>
                </DialogHeader>
                <TemplateForm 
                  template={selectedTemplate || undefined} 
                  type={type} 
                  onSave={handleSaveTemplate} 
                  onCancel={handleCancelForm} 
                />
              </DialogContent>
            </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <TemplateTabs activeTab={activeTab} onTabChange={setActiveTab}>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p className="text-muted-foreground">Loading templates...</p>
            </div>
          ) : filteredTemplates.length > 0 ? (
            <TemplateList
              templates={filteredTemplates}
              onSelectTemplate={onSelectTemplate}
              onCopyToClipboard={handleCopyToClipboard}
              onEditTemplate={handleEditTemplate}
              onDeleteTemplate={confirmDeleteTemplate}
              onSendEmail={type === 'email' ? handleSendEmail : undefined}
            />
          ) : (
            <div className="text-center py-12 space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <p className="text-base font-medium text-foreground mb-1">
                  No templates yet
                </p>
                <p className="text-sm text-muted-foreground">
                  Create your first {type === 'email' ? 'email template' : 'call script'} to get started
                </p>
              </div>
              <Button 
                onClick={() => setIsCreating(true)} 
                variant="outline"
                size="sm"
                className="border-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>
          )}
        </TemplateTabs>
      </CardContent>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTemplateToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTemplate}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {templateForEmail && (
        <SendEmailDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          leadId={leadId || ''}
          leadName={leadData?.name || ''}
          leadEmail={getLeadEmailString(leadData?.email)}
          defaultSubject={extractSubjectFromContent(templateForEmail.content)}
          defaultBody={extractBodyFromContent(templateForEmail.content)}
        />
      )}
      </Card>
    </TooltipProvider>
  );
};

export default TemplateManager;
